<template>
  <!-- Lisa uus klient / Muuda in the new look (Seaded -> Kliendid uues
       kujunduses), agreed on a mockup first: the type as two buttons, the form
       in sections, a client that may already exist said while typing, the
       address from the address register, and a card with the client as it
       will be saved, what is still missing, and Salvesta. -->
  <div class="cf">
    <div class="cf-cap">Kliendid</div>
    <h1 class="cf-title">{{ editing ? 'Muuda klienti' : 'Lisa uus klient' }}<span v-if="editing && original" class="cf-title-sub"> · {{ original.fullName }}</span></h1>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" />
    <div v-else class="cf-layout">
      <div>
        <section class="cf-sec">
          <h2><span class="cf-num">1</span>Kes on klient?</h2>
          <div class="cf-types" role="radiogroup" aria-label="Kliendi tüüp">
            <button
              v-for="option in typeOptions"
              :key="option.value"
              type="button"
              class="cf-type"
              :class="{ on: form.type === option.value }"
              role="radio"
              :aria-checked="form.type === option.value ? 'true' : 'false'"
              @click="form.type = option.value"
            >
              <span class="cf-ic"><v-icon size="18">{{ option.icon }}</v-icon></span>
              <span><b>{{ option.text }}</b><span>{{ option.hint }}</span></span>
            </button>
          </div>
        </section>

        <section class="cf-sec">
          <h2><span class="cf-num">2</span>Nimi ja kontakt</h2>
          <div class="cf-g2">
            <template v-if="!isCompany">
              <label class="cf-fld"><span class="cf-lbl">Eesnimi <span class="cf-req">*</span></span><input v-model="form.firstName" class="cf-in" autocomplete="off"></label>
              <label class="cf-fld"><span class="cf-lbl">Perenimi <span class="cf-req">*</span></span><input v-model="form.lastName" class="cf-in" autocomplete="off"></label>
            </template>
            <label v-else class="cf-fld cf-full"><span class="cf-lbl">Firma nimi <span class="cf-req">*</span></span><input v-model="form.title" class="cf-in" autocomplete="off" placeholder="nt KÜ Kase 12 või Kodukatel OÜ"></label>
            <label class="cf-fld">
              <span class="cf-lbl">Telefon <span class="cf-req">*</span></span>
              <input v-model="form.phone" class="cf-in" inputmode="tel" autocomplete="off" placeholder="5123 4567">
              <!-- The phone is the customer portal's password
                   (auth/userStrategy.js 'client'): it is never left empty. -->
              <span class="cf-hint">Kliendiportaali sisselogimine käib lepingunumbri ja selle telefoniga.</span>
            </label>
            <label class="cf-fld">
              <span class="cf-lbl">E-post</span>
              <input v-model="form.email" class="cf-in" :class="{ bad: !emailOk }" type="email" autocomplete="off" placeholder="nimi@näide.ee">
              <span v-if="!emailOk" class="cf-err">E-posti aadress pole õige kujuga.</span>
            </label>
            <div v-if="isCompany" class="cf-fld cf-full">
              <span class="cf-lbl">Kontaktisik</span>
              <autocomplete v-model="form.contact" item-text="fullName" item-value="id" searchMethod="searchClients" itemKey="clients" variant="outlined" density="compact" hide-details="auto" placeholder="Otsi olemasolevat klienti nime järgi">
                <template v-if="!(form.contact && form.contact.id)" #append-inner><contact-add :typed="form.contact" :country-id="form.countryId" :city-id="form.cityId" @created="form.contact = $event" /></template>
              </autocomplete>
              <span class="cf-hint">Inimene, kellega firma asjus suhelda. Vali olemasolev klient või lisa uus nupust Lisa kontakt.</span>
            </div>
          </div>
        </section>

        <div v-if="matches.length && !dupIgnored" class="cf-warn" role="status">
          <div><b>Selline klient võib juba olemas olla.</b> Kattub {{ matchedOn }}.</div>
          <div v-for="match in matches" :key="match.id" class="cf-who">
            <router-link :to="{ name: 'client/view/info', params: { clientId: match.id } }" target="_blank" class="cf-link">
              <mark v-if="match.on.includes('name')">{{ match.fullName }}</mark><template v-else>{{ match.fullName }}</template>
            </router-link>
            <span><mark v-if="match.on.includes('phone')">{{ match.phone }}</mark><template v-else>{{ match.phone || '—' }}</template></span>
            <span><mark v-if="match.on.includes('email')">{{ match.email }}</mark><template v-else>{{ match.email || '' }}</template></span>
            <span class="cf-hint">{{ match.objects }} objekti · {{ match.works }} töökäsku<template v-if="match.address"> · {{ [match.address, match.city].filter(Boolean).join(', ') }}</template></span>
          </div>
          <div class="cf-acts">
            <v-btn variant="outlined" size="small" :to="{ name: 'client/view/info', params: { clientId: matches[0].id } }">Ava olemasolev</v-btn>
            <v-btn variant="outlined" size="small" @click="dupIgnored = true">See on teine klient, jätka</v-btn>
          </div>
        </div>

        <section class="cf-sec">
          <h2><span class="cf-num">3</span>Aadress <span class="cf-hint cf-h2hint">arvete jaoks; objekti aadress lisatakse objektile</span></h2>
          <div class="cf-g2">
            <div class="cf-fld cf-full">
              <span class="cf-lbl">Aadress <span v-if="fromAds.address && fromAds.address === form.address" class="cf-mark">aadressregistrist</span></span>
              <autocomplete
                v-model="addressSearch"
                searchMethod="searchAdsAddresses"
                itemKey="objects"
                item-text="fullAddress"
                hide-no-data
                variant="outlined"
                density="compact"
                hide-details="auto"
                placeholder="Hakka kirjutama, valime aadressregistrist"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.streetAddress || item.raw.fullAddress" :subtitle="item.raw.fullAddress" />
                </template>
              </autocomplete>
            </div>
            <label class="cf-fld">
              <span class="cf-lbl">Linn <span class="cf-req">*</span> <span v-if="fromAds.cityId && fromAds.cityId === form.cityId" class="cf-mark">aadressist</span></span>
              <select v-model="form.cityId" class="cf-in">
                <option v-for="city in cities" :key="city.id" :value="city.id">{{ city.title }}</option>
              </select>
            </label>
            <label class="cf-fld">
              <span class="cf-lbl">Postiindeks <span v-if="fromAds.postcode && fromAds.postcode === form.postcode" class="cf-mark">aadressist</span></span>
              <input v-model="form.postcode" class="cf-in" inputmode="numeric" autocomplete="off">
            </label>
            <label class="cf-fld">
              <span class="cf-lbl">Riik</span>
              <select v-model="form.countryId" class="cf-in">
                <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.title }}</option>
              </select>
            </label>
          </div>
        </section>

        <section class="cf-sec">
          <h2><span class="cf-num">4</span>Märkimiseks</h2>
          <button type="button" class="cf-switch" role="switch" :aria-checked="form.problematic ? 'true' : 'false'" @click="form.problematic = !form.problematic">
            <span class="cf-track" />
            <span><b>Probleemne klient</b><br><span class="cf-hint">Nimi on nimekirjades punane ja töökäsul on hoiatus.</span></span>
          </button>
          <!-- Hidden with the switch off, but the text stays: switched back on,
               the same reason returns. -->
          <label v-if="form.problematic" class="cf-fld cf-reason">
            <span class="cf-lbl">Põhjus</span>
            <textarea v-model="form.problematicNote" class="cf-in" rows="3" maxlength="1000" />
            <span class="cf-hint cf-reason-foot">
              <span>Näevad kontor ja tehnikud. Klient portaalis ei näe.</span>
              <span>{{ (form.problematicNote || '').length }} / 1000</span>
            </span>
          </label>
        </section>
      </div>

      <aside class="cf-card" aria-label="Kokkuvõte">
        <div class="cf-card-head">
          <div class="cf-cap">{{ isCompany ? 'Firma või KÜ' : 'Eraklient' }}</div>
          <div class="cf-card-name" :class="{ empty: !name }">{{ name || 'Nimi on veel sisestamata' }}</div>
          <div v-if="form.problematic && form.problematicNote" class="cf-card-reason">{{ form.problematicNote }}</div>
        </div>
        <dl class="cf-card-body">
          <div class="cf-row"><dt>Telefon</dt><dd>{{ form.phone || '' }}<span v-if="!form.phone" class="cf-miss">puudub</span></dd></div>
          <div class="cf-row"><dt>E-post</dt><dd>{{ form.email || '' }}<span v-if="!form.email" class="cf-miss">puudub</span></dd></div>
          <div class="cf-row"><dt>Aadress</dt><dd>{{ addressLine }}<span v-if="!addressLine" class="cf-miss">puudub</span></dd></div>
          <div v-if="isCompany && form.contact && form.contact.fullName" class="cf-row"><dt>Kontakt</dt><dd>{{ form.contact.fullName }}</dd></div>
        </dl>
        <div class="cf-checks">
          <div v-for="item in checklist" :key="item.text" :class="item.ok ? 'ok' : item.tone">{{ item.ok ? '✓' : '○' }} {{ item.text }}</div>
        </div>
        <div class="cf-card-foot">
          <v-btn color="green" variant="flat" block :disabled="!ready" :loading="saving === 'save'" @click="save(false)">Salvesta</v-btn>
          <v-btn v-if="!editing" variant="outlined" block :disabled="!ready" :loading="saving === 'object'" @click="save(true)">Salvesta ja lisa objekt</v-btn>
          <v-btn variant="text" block @click="cancel">Katkesta</v-btn>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import { matchCityId } from '@/plugins/adsCity'

const PRIVATE = 1
const COMPANY = 2
const empty = () => ({
  type: PRIVATE,
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  address: '',
  postcode: '',
  countryId: 1,
  cityId: 1,
  contact: undefined,
  problematic: false,
  problematicNote: ''
})
const digits = s => String(s || '').replace(/\D/g, '').replace(/^372/, '')

export default {
  data () {
    return {
      form: empty(),
      original: null,
      loading: false,
      saving: null,
      countries: [],
      cities: [],
      addressSearch: '',
      fromAds: {},
      matches: [],
      dupIgnored: false,
      checkNo: 0,
      typeOptions: [
        { value: PRIVATE, text: 'Eraklient', icon: 'mdi-account', hint: 'Inimene, kelle kodus on küte. Ees- ja perenimi.' },
        { value: COMPANY, text: 'Firma või KÜ', icon: 'mdi-domain', hint: 'Ettevõte, korteriühistu, haldur. Nimi ja kontaktisik.' }
      ]
    }
  },
  computed: {
    clientId () {
      return this.$route.params.clientId
    },
    editing () {
      return !!this.clientId
    },
    isCompany () {
      return this.form.type === COMPANY
    },
    name () {
      return this.isCompany ? (this.form.title || '').trim() : [this.form.firstName, this.form.lastName].map(s => (s || '').trim()).filter(Boolean).join(' ')
    },
    emailOk () {
      const v = (this.form.email || '').trim()
      return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    },
    // A new client's phone has to be a real number; an old one with a short
    // landline can still be re-saved, as long as it is not emptied.
    phoneOk () {
      return this.editing ? !!(this.form.phone || '').trim() : digits(this.form.phone).length >= 7
    },
    addressLine () {
      const city = (this.cities.find(c => c.id === this.form.cityId) || {}).title
      return this.form.address ? [this.form.address, this.form.postcode, city].filter(Boolean).join(', ') : ''
    },
    matchedOn () {
      const label = { phone: 'telefon', email: 'e-post', name: 'nimi' }
      return [...new Set(this.matches.flatMap(m => m.on))].map(k => label[k]).join(', ')
    },
    checklist () {
      return [
        { ok: !!this.name, text: this.isCompany ? 'Firma nimi' : 'Ees- ja perenimi', tone: 'no' },
        { ok: this.phoneOk, text: this.editing ? 'Telefon' : 'Telefon (vähemalt 7 numbrit)', tone: 'no' },
        { ok: this.emailOk, text: 'E-post õige kujuga või tühi', tone: 'no' },
        { ok: !this.matches.length || this.dupIgnored, text: 'Sarnast klienti pole või on kinnitatud, et see on teine', tone: 'amber' }
      ]
    },
    ready () {
      return this.checklist.every(item => item.ok) && !this.saving
    },
    // What the existing-client check is asked about.
    checkKey () {
      return JSON.stringify([this.form.type, this.form.firstName, this.form.lastName, this.form.title, this.form.phone, this.form.email])
    }
  },
  watch: {
    clientId () {
      this.refresh()
    },
    checkKey () {
      this.dupIgnored = false
      this.check()
    },
    async 'form.countryId' (countryId) {
      if (!countryId) return
      const cityId = this.form.cityId
      this.cities = await api.getCities(countryId)
      if (!this.cities.find(({ id }) => id === cityId)) this.form.cityId = this.cities[0] && this.cities[0].id
    },
    addressSearch (val) {
      if (val && typeof val === 'object') {
        // The street part only; the city is kept separately.
        this.form.address = val.streetAddress || ''
        this.form.postcode = val.postcode || ''
        const cityId = matchCityId(this.cities, val)
        if (cityId) this.form.cityId = cityId
        this.fromAds = { address: this.form.address, postcode: this.form.postcode, cityId: cityId || null }
      } else {
        this.form.address = val || ''
      }
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async refresh () {
      this.form = empty()
      this.original = null
      this.addressSearch = ''
      this.fromAds = {}
      this.matches = []
      if (!this.clientId) return
      this.loading = true
      try {
        const { client } = await api.getClient(this.clientId)
        this.original = client
        Object.assign(this.form, {
          type: client.type,
          firstName: client.firstName || '',
          lastName: client.lastName || '',
          title: client.title || '',
          email: client.email || '',
          phone: client.phone || '',
          address: client.address || '',
          postcode: client.postcode || '',
          countryId: client.countryId || 1,
          cityId: client.cityId || 1,
          contact: client.contact || undefined,
          problematic: !!client.problematic,
          problematicNote: client.problematicNote || ''
        })
        this.addressSearch = client.address || ''
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    async runCheck () {
      const no = ++this.checkNo
      try {
        const { matches } = await api.checkClient({
          type: this.form.type,
          firstName: this.form.firstName || undefined,
          lastName: this.form.lastName || undefined,
          title: this.form.title || undefined,
          phone: this.form.phone || undefined,
          email: this.form.email || undefined,
          excludeId: this.clientId || undefined
        })
        if (no === this.checkNo) this.matches = matches
      } catch (err) {
        if (no === this.checkNo) this.matches = []
      }
    },
    async save (thenObject) {
      if (!this.ready) return
      this.saving = thenObject ? 'object' : 'save'
      const request = {
        type: this.form.type,
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        title: this.form.title,
        email: (this.form.email || '').trim(),
        phone: (this.form.phone || '').trim(),
        address: this.form.address,
        postcode: this.form.postcode,
        countryId: this.form.countryId,
        cityId: this.form.cityId,
        problematic: !!this.form.problematic,
        problematicNote: this.form.problematicNote || null,
        // null empties it: left out, the server keeps the stored Kontakt.
        contactId: this.isCompany ? ((this.form.contact && this.form.contact.id) || null) : undefined
      }
      try {
        if (this.editing) {
          await api.updateClient(this.clientId, request)
          this.showNotification({ text: 'Klient edukalt uuendatud', color: 'success' })
          this.$router.push({ name: 'client/view/info', params: { clientId: this.clientId } })
        } else {
          const { client } = await api.createClient(request)
          this.showNotification({ text: 'Uus klient edukalt loodud', color: 'success' })
          this.$router.push(thenObject
            ? { name: 'object/create', query: { clientId: client.id } }
            : { name: 'client/view/info', params: { clientId: client.id } })
        }
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.saving = null
      }
    },
    cancel () {
      if (this.editing) this.$router.push({ name: 'client/view/info', params: { clientId: this.clientId } })
      else this.$router.push({ name: 'client/list', params: { type: 'all' } })
    }
  },
  created () {
    this.check = debounce(this.runCheck, 400)
  },
  mounted () {
    this.refresh()
    api.getCountries().then(countries => { this.countries = countries })
    api.getCities(1).then(cities => { this.cities = cities })
  },
  beforeUnmount () {
    this.check.cancel()
  }
}
</script>

<style scoped>
.cf {
  --f-ground: #F4F6F5; --f-surface: #FFFFFF; --f-ink: #16322A; --f-ink-2: #3E504A; --f-ink-3: #5B6B66;
  --f-line: #C9D2CE; --f-line-soft: #E1E7E4; --f-green: #2E7D32; --f-green-dark: #1B5E20; --f-green-bg: #EAF3EA;
  --f-blue: #1F5FD1; --f-blue-bg: #EEF3FC; --f-amber: #8F5400; --f-amber-bg: #FFF4E0; --f-red: #B3261E;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--f-ink); max-width: 1180px; padding-bottom: 40px;
}
.cf button, .cf input, .cf select { font: inherit; color: inherit; }
.cf-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--f-ink-3); font-weight: 600; }
.cf-title { font-size: 30px; font-weight: 600; margin: 2px 0 16px; letter-spacing: -.01em; line-height: 1.2; }
.cf-title-sub { color: var(--f-ink-3); font-weight: 400; }
.cf-layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 18px; align-items: start; }
.cf-sec { background: var(--f-surface); border: 1px solid var(--f-line-soft); border-radius: 10px; padding: 16px; margin-bottom: 14px; }
.cf-sec h2 { font-size: 16px; font-weight: 600; margin: 0 0 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cf-num { width: 22px; height: 22px; border-radius: 50%; background: var(--f-ink); color: #fff; font-size: 12px; display: grid; place-items: center; }
.cf-h2hint { font-weight: 400; }
.cf-types { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.cf-type { border: 1px solid var(--f-line); background: var(--f-surface); border-radius: 10px; padding: 12px 14px; text-align: left; cursor: pointer; display: flex; gap: 12px; align-items: start; }
.cf-type b { display: block; font-size: 15px; }
.cf-type span span { color: var(--f-ink-3); font-size: 13px; }
.cf-ic { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; background: var(--f-line-soft); color: var(--f-ink-2); flex: none; }
.cf-type.on { border-color: var(--f-green); box-shadow: inset 0 0 0 1px var(--f-green); background: #F2F8F2; }
.cf-type.on .cf-ic { background: var(--f-green); color: #fff; }
.cf-g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: start; }
.cf-fld { display: grid; gap: 4px; min-width: 0; }
.cf-full { grid-column: 1 / -1; }
.cf-lbl { font-size: 12px; color: var(--f-ink-3); font-weight: 600; letter-spacing: .02em; display: flex; gap: 6px; align-items: center; }
.cf-req { color: var(--f-red); }
.cf-in { border: 1px solid var(--f-line); border-radius: 8px; padding: 9px 11px; background: var(--f-surface); width: 100%; min-width: 0; appearance: auto; }
.cf-in:focus { outline: 2px solid var(--f-blue); outline-offset: 0; border-color: var(--f-blue); }
.cf-in.bad { border-color: var(--f-red); }
.cf-hint { font-size: 12px; color: var(--f-ink-3); }
.cf-reason { margin: 8px 0 0 48px; }
.cf-reason textarea { resize: vertical; font: inherit; }
.cf-card-reason { margin-top: 8px; padding: 8px 10px; border-radius: 8px; background: var(--f-amber-bg); color: #5B3700; font-size: 13px; line-height: 1.4; white-space: pre-line; }
.cf-reason-foot { display: flex; justify-content: space-between; gap: 12px; }
.cf-err { font-size: 12px; color: var(--f-red); }
.cf-mark { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--f-blue); background: var(--f-blue-bg); padding: 1px 6px; border-radius: 999px; }
.cf-warn { background: var(--f-amber-bg); border: 1px solid rgba(143, 84, 0, .35); border-radius: 10px; padding: 12px 14px; margin-bottom: 14px; display: grid; gap: 8px; }
.cf-warn b { color: var(--f-amber); }
.cf-who { display: flex; flex-wrap: wrap; gap: 6px 14px; align-items: center; background: var(--f-surface); border-radius: 8px; padding: 8px 10px; }
.cf-link { color: var(--f-blue); text-decoration: none; font-weight: 600; }
.cf-acts { display: flex; flex-wrap: wrap; gap: 8px; }
mark { background: #FFE7A3; color: inherit; border-radius: 3px; padding: 0 2px; }
.cf-switch { display: flex; gap: 10px; align-items: center; border: 0; background: none; cursor: pointer; padding: 4px 0; text-align: left; }
.cf-track { width: 38px; height: 22px; border-radius: 999px; background: var(--f-line); position: relative; flex: none; transition: background .15s; }
.cf-track::after { content: ''; position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .15s; }
.cf-switch[aria-checked="true"] .cf-track { background: var(--f-red); }
.cf-switch[aria-checked="true"] .cf-track::after { transform: translateX(16px); }
.cf-card { position: sticky; top: calc(var(--v-layout-top, 0px) + 16px); background: var(--f-surface); border: 1px solid var(--f-line-soft); border-radius: 10px; overflow: hidden; }
.cf-card-head { padding: 14px 16px; border-bottom: 1px solid var(--f-line-soft); }
.cf-card-name { font-size: 18px; font-weight: 600; margin-top: 4px; overflow-wrap: anywhere; }
.cf-card-name.empty { color: var(--f-ink-3); font-weight: 400; }
.cf-card-body { padding: 12px 16px; display: grid; gap: 8px; font-size: 13px; margin: 0; }
.cf-row { display: grid; grid-template-columns: 70px minmax(0, 1fr); gap: 8px; }
.cf-row dt { color: var(--f-ink-3); }
.cf-row dd { margin: 0; overflow-wrap: anywhere; }
.cf-miss { color: var(--f-ink-3); font-style: italic; }
.cf-checks { display: grid; gap: 4px; padding: 0 16px 12px; font-size: 13px; }
.cf-checks .ok { color: var(--f-green-dark); }
.cf-checks .no { color: var(--f-ink-3); }
.cf-checks .amber { color: var(--f-amber); }
.cf-card-foot { padding: 12px 16px; border-top: 1px solid var(--f-line-soft); display: grid; gap: 8px; background: #F8FAF9; }
@media (max-width: 1000px) { .cf-layout { grid-template-columns: minmax(0, 1fr); } .cf-card { position: static; } }
@media (max-width: 760px) { .cf-g2, .cf-types { grid-template-columns: minmax(0, 1fr); } }
</style>
